import Vue from "vue";
import { interactionsService } from "../services/interactions.service";
import { EntityType } from "../constants/entity";

function equalsIgnoreCase(a, b) {
  if (a == null || b == null) return a === b;
  return String(a).toLowerCase() === String(b).toLowerCase();
}

// Polymorphic interaction state keyed by (entityType, entityId).

const entityKey = (type, id) => `${type}:${id}`;

// Tree walkers — comment buckets are 2-level trees, so we walk both
// levels on every mutation (a depth-1 reply is a child of a top-level
// comment, not in bucket.comments directly).
function appendChildInTree(commentTree, parentId, newChild) {
  return commentTree.map((node) => {
    if (node.id === parentId) {
      // Bump childCount alongside the array so "View N replies" stays
      // honest after a local post. Pagination is oldest-first so the
      // new reply naturally lands at the end — no resort needed.
      return {
        ...node,
        children: [...(node.children || []), newChild],
        childCount: (node.childCount || 0) + 1,
      };
    }
    if (node.children && node.children.length) {
      return { ...node, children: appendChildInTree(node.children, parentId, newChild) };
    }
    return node;
  });
}

function patchInTree(commentTree, targetId, patch) {
  return commentTree.map((node) => {
    if (node.id === targetId) return { ...node, ...patch };
    if (node.children && node.children.length) {
      return { ...node, children: patchInTree(node.children, targetId, patch) };
    }
    return node;
  });
}

// Removing a child also decrements the parent's childCount so the
// "View N replies" affordance stays honest without a refetch.
function removeFromTree(commentTree, targetId) {
  const remaining = [];
  for (const node of commentTree) {
    if (node.id === targetId) continue;
    if (node.children && node.children.length) {
      const childrenBefore = node.children.length;
      const childrenAfter  = removeFromTree(node.children, targetId);
      const removedCount   = childrenBefore - childrenAfter.length;
      if (removedCount > 0) {
        remaining.push({
          ...node,
          children: childrenAfter,
          childCount: Math.max(0, (node.childCount || 0) - removedCount),
        });
      } else {
        remaining.push({ ...node, children: childrenAfter });
      }
    } else {
      remaining.push(node);
    }
  }
  return remaining;
}

// Append a paginated batch of children. Dedupes by ID and sorts ASC
// so a reply posted locally during pagination slots in chronologically.
// Stashes the cursor + page-fetched flag on the parent for the next
// click and the has-more decision in CommentItem.
function appendChildrenInTree(commentTree, parentId, incomingChildren, nextPageToken) {
  return commentTree.map((node) => {
    if (node.id === parentId) {
      const existingChildIds = new Set((node.children || []).map((existingChild) => existingChild.id));
      const dedupedNewChildren = (incomingChildren || []).filter(
        (incomingChild) => incomingChild && incomingChild.id != null && !existingChildIds.has(incomingChild.id)
      );
      const mergedChildren = [...(node.children || []), ...dedupedNewChildren];
      mergedChildren.sort((leftChild, rightChild) => (leftChild.id || 0) - (rightChild.id || 0));
      return {
        ...node,
        children: mergedChildren,
        childrenNextPageToken: nextPageToken || null,
        // Distinguishes "server has paginated" from "children array has
        // entries" — a local optimistic post populates the latter without
        // proving the former. CommentItem.hasMoreRepliesToLoad reads this.
        childrenPageFetched: true,
      };
    }
    if (node.children && node.children.length) {
      return {
        ...node,
        children: appendChildrenInTree(node.children, parentId, incomingChildren, nextPageToken),
      };
    }
    return node;
  });
}

const state = {
  // key → { comments[], nextPageToken, loading }
  commentsByEntity: {},
};

const getters = {
  commentsForEntity: (state) => (entityType, entityId) =>
    state.commentsByEntity[entityKey(entityType, entityId)] || {
      comments: [],
      nextPageToken: null,
      isLoading: false,
    },
};

const actions = {
  // Toggles the reaction on any entity. For thread entities we bubble the
  // ReactionResult up to the forum module so its feed/thread-detail entries
  // can reflect the new reactionCount/hasReacted without re-fetching.
  async toggleReaction({ commit, dispatch }, { entityType, entityId }) {
    const result = await interactionsService.toggleReaction({ entityType, entityId });
    if (equalsIgnoreCase(entityType, EntityType.Comment)) {
      commit("applyReactionToComment", result);
    }
    if (equalsIgnoreCase(entityType, EntityType.Thread)) {
      dispatch("forum/applyReactionResult", result, { root: true });
    }
    return result;
  },

  async fetchComments({ commit, state }, { entityType, entityId, reload = false } = {}) {
    const key = entityKey(entityType, entityId);
    const current = state.commentsByEntity[key];
    if (current?.isLoading) return;

    // Reset before flipping isLoading — otherwise the reset would
    // clobber the flag.
    if (reload) commit("resetCommentsForEntity", key);
    commit("setCommentsLoading", { key, isLoading: true });

    try {
      const token = reload ? null : current?.nextPageToken || null;
      const { payload, nextPageToken } = await interactionsService.fetchComments({
        entityType,
        entityId,
        nextPageToken: token,
      });

      if (reload) {
        commit("setCommentsForEntity", { key, comments: payload });
      } else {
        commit("appendCommentsForEntity", { key, comments: payload });
      }
      commit("setCommentsNextPageToken", { key, nextPageToken });
      return payload;
    } finally {
      commit("setCommentsLoading", { key, isLoading: false });
    }
  },

  async addComment({ commit, dispatch }, { entityType, entityId, body, attachmentIds = [], rootEntityType = null, rootEntityId = null }) {
    const comment = await interactionsService.addComment({
      entityType,
      entityId,
      body,
      attachmentIds,
    });

    const isNestedReply = equalsIgnoreCase(entityType, EntityType.Comment);

    if (!isNestedReply) {
      const key = entityKey(entityType, entityId);
      commit("prependCommentForEntity", { key, comment });
      if (equalsIgnoreCase(entityType, EntityType.Thread)) {
        dispatch(
          "forum/bumpThreadCommentCount",
          { threadId: entityId, delta: 1 },
          { root: true }
        );
      }
    } else if (rootEntityType != null && rootEntityId != null) {
      const key = entityKey(rootEntityType, rootEntityId);
      commit("appendChildForParent", { key, parentId: entityId, comment });
    }

    return comment;
  },

  async deleteComment({ commit, dispatch }, { commentId, rootEntityType = null, rootEntityId = null }) {
    const result = await interactionsService.deleteComment(commentId);
    const isDeleted = !!(result && result.isDeleted);
    const descendantCount = (result && result.descendantCount) || 0;

    if (isDeleted && rootEntityType != null && rootEntityId != null) {
      const key = entityKey(rootEntityType, rootEntityId);
      commit("removeCommentFromEntity", { key, commentId });
      if (equalsIgnoreCase(rootEntityType, EntityType.Thread)) {
        dispatch(
          "forum/bumpThreadCommentCount",
          { threadId: rootEntityId, delta: -(1 + descendantCount) },
          { root: true }
        );
      }
    }
    return result;
  },

  async updateComment({ commit }, { id, body, attachmentIds = [], rootEntityType = null, rootEntityId = null }) {
    const updated = await interactionsService.updateComment({ id, body, attachmentIds });
    if (rootEntityType != null && rootEntityId != null) {
      const key = entityKey(rootEntityType, rootEntityId);
      // PUT response carries children:[] which would clobber locally-loaded replies on spread.
      const patch = { ...updated };
      delete patch.children;
      commit("replaceCommentInEntity", { key, comment: patch });
    }
    return updated;
  },

  async setEntityPermissions(_ctx, payload) {
    return await interactionsService.setEntityPermissions(payload);
  },

  async fetchMoreReplies({ commit }, { rootEntityType, rootEntityId, parentId, nextPageToken = null } = {}) {
    const { payload, nextPageToken: newToken } = await interactionsService.fetchComments({
      entityType: EntityType.Comment,
      entityId: parentId,
      nextPageToken,
      pageSize: 10,
    });
    commit("appendChildrenToParent", {
      key: entityKey(rootEntityType, rootEntityId),
      parentId,
      children: payload || [],
      nextPageToken: newToken,
    });
    return payload;
  },
};

const mutations = {
  // ---------- Comments ----------
  resetCommentsForEntity(state, key) {
    Vue.set(state.commentsByEntity, key, {
      comments: [],
      nextPageToken: null,
      isLoading: false,
    });
  },
  setCommentsForEntity(state, { key, comments }) {
    const entry = { ...(state.commentsByEntity[key] || {}), comments };
    Vue.set(state.commentsByEntity, key, entry);
  },
  appendCommentsForEntity(state, { key, comments }) {
    const current = state.commentsByEntity[key]?.comments || [];
    const entry = {
      ...(state.commentsByEntity[key] || {}),
      comments: [...current, ...comments],
    };
    Vue.set(state.commentsByEntity, key, entry);
  },
  setCommentsNextPageToken(state, { key, nextPageToken }) {
    const entry = { ...(state.commentsByEntity[key] || {}), nextPageToken };
    Vue.set(state.commentsByEntity, key, entry);
  },
  setCommentsLoading(state, { key, isLoading }) {
    const entry = { ...(state.commentsByEntity[key] || {}), isLoading };
    Vue.set(state.commentsByEntity, key, entry);
  },
  prependCommentForEntity(state, { key, comment }) {
    const bucket = state.commentsByEntity[key] || { comments: [] };
    Vue.set(state.commentsByEntity, key, {
      ...bucket,
      comments: [comment, ...bucket.comments],
    });
  },
  appendChildForParent(state, { key, parentId, comment }) {
    const bucket = state.commentsByEntity[key];
    if (!bucket?.comments) return;
    const updatedComments = appendChildInTree(bucket.comments, parentId, comment);
    Vue.set(state.commentsByEntity, key, { ...bucket, comments: updatedComments });
  },
  // Append a paginated batch of children to a specific parent in the
  // tree and stash the next-page cursor on the parent node so the next
  // "Load more" click knows where to continue from. 
  appendChildrenToParent(state, { key, parentId, children, nextPageToken }) {
    const bucket = state.commentsByEntity[key];
    if (!bucket?.comments) return;
    const updatedComments = appendChildrenInTree(bucket.comments, parentId, children || [], nextPageToken || null);
    Vue.set(state.commentsByEntity, key, { ...bucket, comments: updatedComments });
  },
  replaceCommentInEntity(state, { key, comment }) {
    const bucket = state.commentsByEntity[key];
    if (!bucket?.comments) return;
    const updatedComments = patchInTree(bucket.comments, comment.id, comment);
    Vue.set(state.commentsByEntity, key, { ...bucket, comments: updatedComments });
  },
  removeCommentFromEntity(state, { key, commentId }) {
    const bucket = state.commentsByEntity[key];
    if (!bucket?.comments) return;
    const updatedComments = removeFromTree(bucket.comments, commentId);
    Vue.set(state.commentsByEntity, key, { ...bucket, comments: updatedComments });
  },

  // ---------- Reactions on comments ----------
  applyReactionToComment(state, { entityID, hasReacted, reactionCount } = {}) {
    Object.keys(state.commentsByEntity).forEach((bucketKey) => {
      const bucket = state.commentsByEntity[bucketKey];
      if (!bucket?.comments) return;
      const updatedComments = patchInTree(bucket.comments, entityID, { hasReacted, reactionCount });
      if (updatedComments !== bucket.comments) {
        Vue.set(state.commentsByEntity, bucketKey, { ...bucket, comments: updatedComments });
      }
    });
  },

};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
